import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { Bell, Send, Users, Star, Clock } from "lucide-react";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

type Segment = "all" | "premium" | "active_7days";

const SEGMENTS: { value: Segment; label: string; icon: React.ReactNode }[] = [
  { value: "all", label: "Tüm Kullanıcılar", icon: <Users size={16} /> },
  { value: "premium", label: "Premium Üyeler", icon: <Star size={16} /> },
  { value: "active_7days", label: "Son 7 Günde Aktif", icon: <Clock size={16} /> },
];

type BroadcastResult = { sent: number; failed: number };

export default function AdminBildirimler() {
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);

  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [segment, setSegment] = useState<Segment>("all");
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const broadcastMutation = useMutation<BroadcastResult, Error>({
    mutationFn: async () => {
      const res = await adminFetch("/api/admin/notifications/broadcast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body, segment }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? "Gönderim başarısız");
      }
      return res.json() as Promise<BroadcastResult>;
    },
    onSuccess: (data) => {
      setResult(data);
      setTitle("");
      setBody("");
    },
  });

  if (!configured) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.error?.message}
      />
    );
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.error?.message}
      />
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950">
      <NumerolojiAdminSidebar
        mode="links"
        isDark={isDark}
        onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
      />
      <main className="flex-1 p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <Bell size={22} className="text-purple-600" />
          <h1 className="text-2xl font-bold">Toplu Bildirim Gönder</h1>
        </div>

        <Card className="p-6 space-y-5">
          {/* Segment */}
          <div>
            <label className="block text-sm font-medium mb-2">Hedef Kitle</label>
            <div className="flex gap-2 flex-wrap">
              {SEGMENTS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSegment(s.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    segment === s.value
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-400"
                  }`}
                >
                  {s.icon}
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Başlık</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bildirim başlığı..."
              maxLength={100}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium mb-1">Mesaj</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Bildirim mesajı..."
              rows={3}
              maxLength={300}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/300</p>
          </div>

          {/* Preview */}
          {(title || body) && (
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Önizleme</p>
              <p className="font-semibold text-sm">{title || "—"}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{body || "—"}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                Gönderildi: {result.sent} kullanıcı
                {result.failed > 0 && <span className="text-red-500 ml-2">— {result.failed} başarısız</span>}
              </p>
            </div>
          )}

          {broadcastMutation.isError && (
            <p className="text-sm text-red-600">{broadcastMutation.error?.message}</p>
          )}

          <Button
            onClick={() => {
              setResult(null);
              broadcastMutation.mutate();
            }}
            disabled={!title.trim() || !body.trim() || broadcastMutation.isPending}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2"
          >
            <Send size={16} />
            {broadcastMutation.isPending ? "Gönderiliyor..." : "Bildirim Gönder"}
          </Button>
        </Card>
      </main>
    </div>
  );
}
